import React, { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';

const CheckoutPage = () => {
  const location = useLocation();
  const { eventId } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    receiveUpdates: false,
    receiveNearbyEvents: false
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(900);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentReference, setPaymentReference] = useState(null);
  
  // Get data passed from ticket selection
  const { ticketSelections = [], eventDetails = {}, total = 0 } = location.state || {};
// Replace your current Paystack implementation with this:

const handlePaystackPayment = () => {
    // Create a unique reference
    const reference = `EVT_${eventId}_${Date.now()}`;
    
    // Store transaction data in session storage as fallback
    sessionStorage.setItem('pendingTransaction', JSON.stringify({
      reference,
      eventId,
      ticketSelections,
      formData,
      total,
      paymentMethod
    }));
  
    // Create payment handler function
    const paymentHandler = (response) => {
      console.log('Paystack response:', response);
      if (response.status === 'success') {
        setPaymentReference(response.reference);
        setPaymentCompleted(true);
      }
    };
  
    // Create script element for Paystack inline implementation
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.onload = () => {
      // @ts-ignore
      const handler = window.PaystackPop.setup({
        key: 'pk_test_c367aa1922990824518e14d2b4ef4a2a983216c0',
        email: formData.email,
        amount: total * 100,
        ref: reference,
        metadata: {
          custom_fields: [
            {
              display_name: "Customer Name",
              variable_name: "customer_name",
              value: formData.fullName
            },
            {
              display_name: "Event ID",
              variable_name: "event_id",
              value: eventId
            }
          ]
        },
        callback: (response) => {
          paymentHandler(response);
        },
        onClose: () => {
          setError('Payment window was closed');
        }
      });
      handler.openIframe();
    };
    
    document.body.appendChild(script);
    
    // Cleanup
    return () => {
      document.body.removeChild(script);
    };
  };
  
  // Modify your handleSubmit to use this new implementation:
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.email || !formData.phoneNumber) {
      setError('Please fill all required fields');
      return;
    }
    
    if (total === 0) {
      handleFreeRegistration();
      return;
    }
    
    // Reset states
    setPaymentCompleted(false);
    setPaymentReference(null);
    setError(null);
    
    // Initialize payment
    handlePaystackPayment();
  };
  
  // Add this useEffect to handle page reloads
  useEffect(() => {
    const pendingTransaction = sessionStorage.getItem('pendingTransaction');
    if (pendingTransaction) {
      const transaction = JSON.parse(pendingTransaction);
      if (window.location.search.includes('reference=')) {
        const urlParams = new URLSearchParams(window.location.search);
        const reference = urlParams.get('reference');
        
        setPaymentReference(reference);
        setPaymentCompleted(true);
        sessionStorage.removeItem('pendingTransaction');
      }
    }
  }, []);

  // Handle payment verification when paymentCompleted changes
  useEffect(() => {
    if (paymentCompleted && paymentReference) {
      verifyPayment(paymentReference);
    }
  }, [paymentCompleted, paymentReference]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => prev <= 1 ? 0 : prev - 1);
    }, 1000);
    
    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTimeLeft = () => {
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Enhanced onSuccess handler
  const onSuccess = (reference) => {
    console.log('Payment successful:', reference);
    setPaymentReference(reference);
    setPaymentCompleted(true);
  };

  const onClose = () => {
    setError('Payment was canceled. Please try again.');
  };

  // Payment verification function
  const verifyPayment = async (reference) => {
    try {
      setLoading(true);
      setError(null);
      
      // Convert price from string to number
      const formattedTickets = ticketSelections.map(ticket => ({
        ticketId: ticket.id, // Ensure this matches your schema
        name: ticket.name,
        type: ticket.type || 'general', // Provide default if needed
        price: parseFloat(ticket.price.replace(/[^\d.-]/g, '')), // Convert string to number
        quantity: ticket.quantity
      }));
  
      const transactionData = {
        reference,
        eventId,
        tickets: formattedTickets,
        customerInfo: {
          name: formData.fullName,
          email: formData.email,
          phone: formData.phoneNumber
        },
        amount: total,
        paymentMethod,
        preferences: {
          receiveUpdates: formData.receiveUpdates,
          receiveNearbyEvents: formData.receiveNearbyEvents
        }
      };
  
      console.log('Sending to backend:', transactionData);
      
      const response = await axios.post('https://event-li-backend.onrender.com/booking/payments/verify', transactionData);
      
      if (response.data) {
        navigate(`/single-event/booking-confirmed/${response.data.bookingId}`, {
          state: { bookingDetails: response.data }
        });
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error("Verification error:", err.response?.data || err.message);
      setError(err.message || 'Payment verification failed');
    } finally {
      setLoading(false);
    }
  };
  const handleFreeRegistration = async (reference) => {
    try {
      setLoading(true);
      
      const registrationData = {
        eventId,
        tickets: ticketSelections,
        customerInfo: {
          name: formData.fullName,
          email: formData.email,
          phone: formData.phoneNumber
        },
        preferences: {
          receiveUpdates: formData.receiveUpdates,
          receiveNearbyEvents: formData.receiveNearbyEvents
        }
      };
      
      const response = await axios.post('https://event-li-backend.onrender.com/booking/bookings/free', registrationData);
      
      navigate(`/single-event/booking-confirmed/${response.data.bookingId}`, {
        state: { bookingDetails: response.data }
      });
      
    } catch (err) {
      setError('Registration failed. Please try again.');
      setLoading(false);
    }
  };
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with Timer */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-semibold text-gray-800">Checkout</h2>
          <div className="text-gray-600 mt-2">
            <span>Time left </span>
            <span className={`font-medium ${timeLeft < 300 ? 'text-red-500' : 'text-gray-800'}`}>
              {formatTimeLeft()}
            </span>
          </div>
        </motion.div>
        
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left column - Billing Form */}
          <motion.div 
            className="w-full lg:w-2/3 bg-white rounded-xl shadow-sm p-6 md:p-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.h3 
              className="text-2xl font-semibold mb-6 text-gray-800"
              variants={itemVariants}
            >
              Billing Information
            </motion.h3>
            
            <form onSubmit={handleSubmit}>
              <motion.div className="space-y-6" variants={containerVariants}>
                <motion.div variants={itemVariants}>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                    Full name
                  </label>
                  <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-300 focus:border-yellow-300 transition-colors"
                    placeholder="Enter full name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                  />
                </motion.div>
                
                <motion.div variants={itemVariants}>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-300 focus:border-yellow-300 transition-colors"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </motion.div>
                
                <motion.div variants={itemVariants}>
                  <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone number
                  </label>
                  <input
                    id="phoneNumber"
                    name="phoneNumber"
                    type="tel"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-300 focus:border-yellow-300 transition-colors"
                    placeholder="Enter phone number"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                  />
                </motion.div>
                
                <motion.div variants={itemVariants} className="space-y-4">
                  <div className="flex items-start">
                    <input
                      id="receiveUpdates"
                      name="receiveUpdates"
                      type="checkbox"
                      className="h-5 w-5 text-yellow-400 rounded border-gray-300 focus:ring-yellow-300"
                      checked={formData.receiveUpdates}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="receiveUpdates" className="ml-3 text-sm text-gray-600">
                      Keep me updated on more event and news from this organizer
                    </label>
                  </div>
                  
                  <div className="flex items-start">
                    <input
                      id="receiveNearbyEvents"
                      name="receiveNearbyEvents"
                      type="checkbox"
                      className="h-5 w-5 text-yellow-400 rounded border-gray-300 focus:ring-yellow-300"
                      checked={formData.receiveNearbyEvents}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="receiveNearbyEvents" className="ml-3 text-sm text-gray-600">
                      Send me emails about the event happening nearby or online
                    </label>
                  </div>
                </motion.div>
                
                {/* Payment Method Selection */}
                {total > 0 && (
                  <motion.div variants={itemVariants} className="pt-4">
                    <h3 className="text-xl font-medium text-gray-800 mb-4">Pay with</h3>
                    
                    <div className="space-y-3">
                      <div className="flex items-center p-4 border border-gray-200 rounded-lg">
                        <input
                          id="card"
                          name="paymentMethod"
                          type="radio"
                          className="h-5 w-5 text-yellow-400 border-gray-300 focus:ring-yellow-300"
                          checked={paymentMethod === 'card'}
                          onChange={() => setPaymentMethod('card')}
                        />
                        <label htmlFor="card" className="ml-3 flex items-center justify-between w-full">
                          <span className="text-gray-700 font-medium">Card</span>
                          <span className="text-gray-500">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <rect x="3" y="5" width="18" height="14" rx="2" stroke="#E63946" strokeWidth="2"/>
                              <line x1="3" y1="10" x2="21" y2="10" stroke="#E63946" strokeWidth="2"/>
                            </svg>
                          </span>
                        </label>
                      </div>
                      
                      <div className="flex items-center p-4 border border-gray-200 rounded-lg">
                        <input
                          id="flutterwave"
                          name="paymentMethod"
                          type="radio"
                          className="h-5 w-5 text-yellow-400 border-gray-300 focus:ring-yellow-300"
                          checked={paymentMethod === 'flutterwave'}
                          onChange={() => setPaymentMethod('flutterwave')}
                        />
                        <label htmlFor="flutterwave" className="ml-3 flex items-center justify-between w-full">
                          <span className="text-gray-700 font-medium">Flutterwave</span>
                          <span className="text-gray-500">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#F5A623" strokeWidth="2"/>
                              <path d="M7 12L10 15L17 8" stroke="#F5A623" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </span>
                        </label>
                      </div>
                      
                      <div className="flex items-center p-4 border border-gray-200 rounded-lg">
                        <input
                          id="paystack"
                          name="paymentMethod"
                          type="radio"
                          className="h-5 w-5 text-yellow-400 border-gray-300 focus:ring-yellow-300"
                          checked={paymentMethod === 'paystack'}
                          onChange={() => setPaymentMethod('paystack')}
                        />
                        <label htmlFor="paystack" className="ml-3 flex items-center justify-between w-full">
                          <span className="text-gray-700 font-medium">Paystack</span>
                          <span className="text-gray-500">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M20 6H4C2.89543 6 2 6.89543 2 8V16C2 17.1046 2.89543 18 4 18H20C21.1046 18 22 17.1046 22 16V8C22 6.89543 21.1046 6 20 6Z" stroke="#00C3F7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" stroke="#00C3F7" strokeWidth="2"/>
                            </svg>
                          </span>
                        </label>
                      </div>
                    </div>
                  </motion.div>
                )}
                
                <motion.div variants={itemVariants} className="pt-4">
                  <div className="text-sm text-gray-600 mb-6">
                    By selecting place order, I agree to the <a href="#" className="text-blue-600 hover:underline">EventLi Terms of Services</a>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 bg-yellow-400 hover:bg-yellow-500 transition-colors rounded-full font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-300 disabled:opacity-70"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </span>
                    ) : (
                      `Check out ${total > 0 ? '- ₦' + total.toLocaleString() : '(Free)'}`
                    )}
                  </button>
                  
                  {error && (
                    <div className="mt-4 text-red-500 text-center">{error}</div>
                  )}
                </motion.div>
              </motion.div>
            </form>
          </motion.div>
          
          {/* Right column - Order Summary */}
          <motion.div 
            className="w-full lg:w-1/3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <div className="bg-white rounded-xl shadow-sm overflow-hidden sticky top-8">
              {/* Event Banner */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={eventDetails.image || "/banner-single-event.png"} 
                  alt={eventDetails.title || "Event banner"} 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex flex-col justify-end p-4">
                  <div className="text-white">
                    <div className="uppercase text-xs font-semibold tracking-wider">BANNER TEMPLATE</div>
                    <div className="text-xs">URBAN MUSIC</div>
                  </div>
                </div>
              </div>
              
              {/* Order Summary */}
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-800 mb-4">Order summary</h3>
                
                <div className="space-y-4">
                  <div className="text-gray-700">
                    {eventDetails.startDate && (
                      <div className="mb-2">
                        {new Date(eventDetails.startDate).toLocaleDateString('en-NG', { 
                          weekday: 'long', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                        {' '}
                        {new Date(eventDetails.startDate).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                        {' - '}
                        {new Date(eventDetails.endDate).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                        {' WAT'}
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    {ticketSelections.map((ticket, index) => (
                      <div key={index} className="flex justify-between">
                        <span>{ticket.quantity} x {ticket.name}</span>
                        <span>₦{(parseFloat(ticket.price.replace(/[^\d.-]/g, '')) * ticket.quantity).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4 space-y-2">
                    <div className="flex justify-between">
                      <span>Subtotal</span>
                      <span>₦{total.toLocaleString()}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span>Fees & Taxes</span>
                      <span>₦0.00</span>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex justify-between items-center text-lg font-semibold">
                      <span>Total</span>
                      <span>₦{total.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {paymentCompleted && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md">
            <h3 className="text-lg font-medium mb-4">Processing Payment</h3>
            <p>Your payment was successful. Verifying with our server...</p>
            {loading && (
              <div className="mt-4 flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            )}
          </div>
        </div>
      )}

{sessionStorage.getItem('pendingTransaction') && (
  <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg max-w-md">
      <h3 className="text-xl font-bold mb-4">Complete Your Payment</h3>
      <p>We detected an unfinished payment. Would you like to continue?</p>
      <div className="mt-4 flex gap-2">
        <button 
          onClick={() => {
            sessionStorage.removeItem('pendingTransaction');
            window.location.reload();
          }}
          className="px-4 py-2 bg-gray-200 rounded"
        >
          Cancel
        </button>
        <button 
          onClick={() => {
            const transaction = JSON.parse(sessionStorage.getItem('pendingTransaction'));
            setFormData(transaction.formData);
            handlePaystackPayment();
          }}
          className="px-4 py-2 bg-yellow-400 rounded"
        >
          Continue Payment
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default CheckoutPage;